import { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';

const PressForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);
    const [formData, setFormData] = useState({
        title: '',
        source: '', // e.g. Muğla Gazetesi
        date: new Date().toLocaleDateString('tr-TR'),
        url: '',
        summary: ''
    });

    useEffect(() => {
        if (isEditMode) {
            const fetchData = async () => {
                const docRef = doc(db, "press", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setFormData(docSnap.data());
                } else {
                    alert("Haber bulunamadı!");
                    navigate('/admin/press');
                }
                setFetching(false);
            };
            fetchData();
        }
    }, [id, isEditMode, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const dataToSave = {
                ...formData,
                updatedAt: new Date().toISOString()
            };

            if (isEditMode) {
                await updateDoc(doc(db, "press", id), dataToSave);
            } else {
                await addDoc(collection(db, "press"), {
                    ...dataToSave,
                    createdAt: new Date().toISOString()
                });
            }

            navigate('/admin/press');
        } catch (error) {
            console.error("Error saving press item:", error);
            alert("Kaydetme sırasında bir hata oluştu: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (fetching) return <div className="text-white">Veri yükleniyor...</div>;

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => navigate('/admin/press')}
                    className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="text-3xl font-bold text-white">
                    {isEditMode ? 'Haberi Düzenle' : 'Yeni Haber Ekle'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="bg-gray-800 rounded-xl border border-gray-700 p-8 space-y-6">

                {/* Title */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Başlık</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                        placeholder="Örn: TURET Etkinliği Basında Yankı Uyandırdı"
                        required
                    />
                </div>

                {/* Source & Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-gray-400 text-sm font-medium mb-2">Kaynak</label>
                        <input
                            type="text"
                            name="source"
                            value={formData.source}
                            onChange={handleChange}
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                            placeholder="Örn: Muğla Gazetesi, Instagram"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-gray-400 text-sm font-medium mb-2">Tarih</label>
                        <input
                            type="text"
                            name="date"
                            value={formData.date}
                            onChange={handleChange}
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                            placeholder="DD.MM.YYYY"
                            required
                        />
                    </div>
                </div>

                {/* URL */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Link (URL)</label>
                    <input
                        type="url"
                        name="url"
                        value={formData.url}
                        onChange={handleChange}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                        placeholder="https://..."
                    />
                </div>

                {/* Summary */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Özet / Açıklama</label>
                    <textarea
                        name="summary"
                        value={formData.summary}
                        onChange={handleChange}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors h-32 resize-none"
                        placeholder="Haber hakkında kısa bir özet..."
                        required
                    />
                </div>

                {/* Submit */}
                <div className="pt-4 border-t border-gray-700 flex justify-end">
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-3 rounded-lg font-bold flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Kaydediliyor...' : (
                            <>
                                <Save size={20} />
                                <span>Kaydet</span>
                            </>
                        )}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default PressForm;
